import type {
  CheckoutResultDto,
  CreditTransactionDto,
  PagedQuery,
  PagedResult,
  PaymentDto,
  PlanDto,
} from "@mandoob/shared";

import {
  type MutationHookOptions,
  type QueryHookOptions,
  type RequestOptions,
  useApiMutation,
  useApiQuery,
} from "@/api/query-helpers";
import { api } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const getPlans = (options?: RequestOptions, signal?: AbortSignal) =>
  api<PlanDto[]>({ url: "/api/plans", method: "GET", signal }, options);

export const getGetPlansQueryKey = () => ["/api/plans"] as const;

export const useGetPlans = (options?: QueryHookOptions<PlanDto[]>) =>
  useApiQuery(
    getGetPlansQueryKey(),
    (request, signal) => getPlans(request, signal),
    options,
  );

// Unauthenticated mirror of /api/plans for the public landing page. 404s when
// the API runs with DISABLE_PAYMENTS — the pricing section then hides itself.
export const getPublicPlans = (
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<PlanDto[]>({ url: "/api/public/plans", method: "GET", signal }, options);

export const useGetPublicPlans = (options?: QueryHookOptions<PlanDto[]>) =>
  useApiQuery(
    ["/api/public/plans"] as const,
    (request, signal) => getPublicPlans(request, signal),
    { ...options, query: { retry: false, ...options?.query } },
  );

export const checkout = (
  planId: string,
  source?: "app",
  options?: RequestOptions,
) =>
  api<CheckoutResultDto>(
    {
      url: `/api/plans/${planId}/checkout`,
      method: "POST",
      ...(source ? { data: { source } } : {}),
    },
    options,
  );

export const useCheckout = (
  options?: MutationHookOptions<
    CheckoutResultDto,
    { planId: string; source?: "app" }
  >,
) =>
  useApiMutation(
    ({ planId, source }, request) => checkout(planId, source, request),
    options,
  );

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

/**
 * Asks the server to re-verify the driver's recent Pending payments and
 * credit any that were paid — fired when the native app returns to the
 * foreground after a browser checkout.
 */
export const reconcileMyPayments = (options?: RequestOptions) =>
  api<{ credited: number }>(
    { url: "/api/payments/reconcile", method: "POST" },
    options,
  );

export const getPayment = (
  paymentId: string,
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<PaymentDto>(
    { url: `/api/payments/${paymentId}`, method: "GET", signal },
    options,
  );

export const getGetPaymentQueryKey = (paymentId: string) =>
  [`/api/payments/${paymentId}`] as const;

export const useGetPayment = (
  paymentId: string,
  options?: QueryHookOptions<PaymentDto>,
) =>
  useApiQuery(
    getGetPaymentQueryKey(paymentId),
    (request, signal) => getPayment(paymentId, request, signal),
    {
      ...options,
      query: { enabled: !!paymentId, ...options?.query },
    },
  );

export const getPayments = (
  params?: PagedQuery,
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<PagedResult<PaymentDto>>(
    { url: "/api/payments", method: "GET", params, signal },
    options,
  );

export const getGetPaymentsQueryKey = (params?: PagedQuery) =>
  ["/api/payments", ...(params ? [params] : [])] as const;

export const useGetPayments = (
  params?: PagedQuery,
  options?: QueryHookOptions<PagedResult<PaymentDto>>,
) =>
  useApiQuery(
    getGetPaymentsQueryKey(params),
    (request, signal) => getPayments(params, request, signal),
    options,
  );

// ---------------------------------------------------------------------------
// Credits ledger
// ---------------------------------------------------------------------------

export const getLedger = (
  params?: PagedQuery,
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<PagedResult<CreditTransactionDto>>(
    { url: "/api/credits/ledger", method: "GET", params, signal },
    options,
  );

export const getGetLedgerQueryKey = (params?: PagedQuery) =>
  ["/api/credits/ledger", ...(params ? [params] : [])] as const;

export const useGetLedger = (
  params?: PagedQuery,
  options?: QueryHookOptions<PagedResult<CreditTransactionDto>>,
) =>
  useApiQuery(
    getGetLedgerQueryKey(params),
    (request, signal) => getLedger(params, request, signal),
    options,
  );
